import { z } from 'zod';
import { firstError } from './formSchemas';

export const COUNTRY_CODES = [
  { code: '+1', country: 'United States', abbr: 'US' },
  { code: '+1', country: 'Canada', abbr: 'CA' },
  { code: '+44', country: 'United Kingdom', abbr: 'GB' },
  { code: '+49', country: 'Germany', abbr: 'DE' },
  { code: '+33', country: 'France', abbr: 'FR' },
  { code: '+39', country: 'Italy', abbr: 'IT' },
  { code: '+34', country: 'Spain', abbr: 'ES' },
  { code: '+31', country: 'Netherlands', abbr: 'NL' },
  { code: '+32', country: 'Belgium', abbr: 'BE' },
  { code: '+43', country: 'Austria', abbr: 'AT' },
  { code: '+41', country: 'Switzerland', abbr: 'CH' },
  { code: '+46', country: 'Sweden', abbr: 'SE' },
  { code: '+47', country: 'Norway', abbr: 'NO' },
  { code: '+45', country: 'Denmark', abbr: 'DK' },
  { code: '+358', country: 'Finland', abbr: 'FI' },
  { code: '+48', country: 'Poland', abbr: 'PL' },
  { code: '+420', country: 'Czech Republic', abbr: 'CZ' },
  { code: '+36', country: 'Hungary', abbr: 'HU' },
  { code: '+40', country: 'Romania', abbr: 'RO' },
  { code: '+359', country: 'Bulgaria', abbr: 'BG' },
  { code: '+30', country: 'Greece', abbr: 'GR' },
  { code: '+351', country: 'Portugal', abbr: 'PT' },
  { code: '+353', country: 'Ireland', abbr: 'IE' },
  { code: '+352', country: 'Luxembourg', abbr: 'LU' },
  { code: '+356', country: 'Malta', abbr: 'MT' },
  { code: '+357', country: 'Cyprus', abbr: 'CY' },
  { code: '+372', country: 'Estonia', abbr: 'EE' },
  { code: '+371', country: 'Latvia', abbr: 'LV' },
  { code: '+370', country: 'Lithuania', abbr: 'LT' },
  { code: '+386', country: 'Slovenia', abbr: 'SI' },
  { code: '+421', country: 'Slovakia', abbr: 'SK' },
  { code: '+385', country: 'Croatia', abbr: 'HR' },
  { code: '+61', country: 'Australia', abbr: 'AU' },
  { code: '+64', country: 'New Zealand', abbr: 'NZ' },
  { code: '+81', country: 'Japan', abbr: 'JP' },
  { code: '+82', country: 'South Korea', abbr: 'KR' },
  { code: '+886', country: 'Taiwan', abbr: 'TW' },
  { code: '+65', country: 'Singapore', abbr: 'SG' },
  { code: '+91', country: 'India', abbr: 'IN' },
  { code: '+972', country: 'Israel', abbr: 'IL' },
  { code: '+90', country: 'Turkey', abbr: 'TR' },
  { code: '+27', country: 'South Africa', abbr: 'ZA' },
  { code: '+55', country: 'Brazil', abbr: 'BR' },
  { code: '+52', country: 'Mexico', abbr: 'MX' },
  { code: '+54', country: 'Argentina', abbr: 'AR' },
  { code: '+56', country: 'Chile', abbr: 'CL' },
  { code: '+57', country: 'Colombia', abbr: 'CO' },
  { code: '+51', country: 'Peru', abbr: 'PE' },
  { code: '+58', country: 'Venezuela', abbr: 'VE' },
  { code: '+598', country: 'Uruguay', abbr: 'UY' },
  { code: '+595', country: 'Paraguay', abbr: 'PY' },
  { code: '+593', country: 'Ecuador', abbr: 'EC' },
  { code: '+506', country: 'Costa Rica', abbr: 'CR' },
  { code: '+507', country: 'Panama', abbr: 'PA' },
  { code: '+503', country: 'El Salvador', abbr: 'SV' },
  { code: '+809', country: 'Dominican Republic', abbr: 'DO' },
  { code: '+377', country: 'Monaco', abbr: 'MC' },
];

const COUNTRY_CODE_OVERRIDES: Record<string, string> = {
  'USA': 'US',
  'UK': 'GB',
};

export function getPhoneCodeForCountry(countryCode: string): string {
  const normalizedCode = COUNTRY_CODE_OVERRIDES[countryCode] || countryCode;
  const match = COUNTRY_CODES.find(
    (entry) => entry.abbr.toLowerCase() === normalizedCode.toLowerCase()
  );
  return match?.code ?? '+1';
}

export function formatUSPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) {
    return '';
  }
  if (digits.length <= 3) {
    return `(${digits}`;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  const basePhone = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  const ext = digits.slice(10);
  return ext ? `${basePhone} ext. ${ext}` : basePhone;
}

export function formatInternationalPhone(value: string, _countryCode: string): string {
  return value.replace(/\D/g, '');
}

const PHONE_REQUIRED = 'Phone number is required';

/** A phone number checked by its digit count; formatting characters are ignored. */
const phoneDigits = (minDigits: number, tooShortMessage: string) =>
  z
    .string({ error: PHONE_REQUIRED })
    .transform((value) => value.replace(/\D/g, ''))
    .pipe(
      z
        .string()
        .min(1, PHONE_REQUIRED)
        .min(minDigits, tooShortMessage)
        .max(15, 'Phone number is too long'),
    );

export const usPhoneSchema = phoneDigits(10, 'Please enter a valid 10-digit phone number');

export const internationalPhoneSchema = phoneDigits(6, 'Please enter a valid phone number');

const toPhoneResult = (schema: z.ZodType, value: string): { isValid: boolean; error?: string } => {
  const result = schema.safeParse(value);
  return result.success ? { isValid: true } : { isValid: false, error: firstError(result.error) };
};

export function validateUSPhone(value: string): { isValid: boolean; error?: string } {
  return toPhoneResult(usPhoneSchema, value);
}

export function validateInternationalPhone(value: string): { isValid: boolean; error?: string } {
  return toPhoneResult(internationalPhoneSchema, value);
}

export function getDisplayPhone(phone: string, countryCode?: string): string {
  if (!phone) {
    return '';
  }
  if (countryCode) {
    const formattedNumber = formatInternationalPhone(phone, countryCode);
    return `${countryCode} ${formattedNumber}`;
  }
  return formatUSPhone(phone);
}
